import { Box, Flex, FormatByte, HStack, IconButton, Text } from '@chakra-ui/react';
import type { BatchAttachmentInfo } from '@fluenceur/types';
import { Attachment, Xmark } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface AttachmentChipsProps {
  attachments: BatchAttachmentInfo[];
  onRemove: (id: string) => void;
  deletingId: string | null;
  removeLabel?: string;
  variant?: 'card' | 'plain';
  disabled?: boolean;
}

const AttachmentChips: React.FC<AttachmentChipsProps> = React.memo(
  ({ attachments, onRemove, deletingId, removeLabel, variant = 'card', disabled }) => {
    const { t } = useTranslation();

    if (attachments.length === 0) return null;

    return (
      <Box
        px={variant === 'plain' ? 0 : 2.5}
        py={variant === 'plain' ? 0 : 2}
        borderTop={variant === 'plain' ? 'none' : '1px solid'}
        borderColor="border"
      >
        <Flex gap={2} flexWrap="wrap" minW={0}>
          {attachments.map((attachment) => (
            <HStack
              key={attachment.id}
              bg="bg.subtle"
              rounded="md"
              maxW={{ base: '100%', md: '320px' }}
              pl={2.5}
              pr={1}
              py={1}
              gap={1.5}
              fontSize="xs"
            >
              <Attachment width={12} height={12} />
              <Text truncate maxW="140px">
                {attachment.name}
              </Text>
              <Text color="fg.muted" flexShrink={0}>
                <FormatByte unitSystem="binary" value={attachment.size} />
              </Text>
              <IconButton
                aria-label={removeLabel ?? t('bulkEmail.modal.attachments.remove')}
                size="2xs"
                variant="ghost"
                rounded="sm"
                colorPalette="gray"
                onClick={() => onRemove(attachment.id)}
                disabled={disabled}
                loading={deletingId === attachment.id}
                minW={5}
                h={5}
              >
                <Xmark width={12} height={12} />
              </IconButton>
            </HStack>
          ))}
        </Flex>
      </Box>
    );
  }
);

AttachmentChips.displayName = 'AttachmentChips';

export default AttachmentChips;
